import { Component, Input, OnInit, OnDestroy, HostListener, inject, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AuthHelper } from '../helpers/auth.helper';
import { ToastService } from '../services/toastr.service';
import { Router } from '@angular/router';
import { UserRole } from '@/app/auth/enums/role.enum';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@/environments/environment';
import { NotificationsService } from '@/app/notifications/services/notifications.service';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
})
export class SidebarComponent implements OnInit, OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly notificationsService = inject(NotificationsService);
  @Input() userLogged: boolean = false;
  isExpanded = false;
  isCompact = false;
  public vitalSignsUrl = environment.monitor;
  public unreadCount$: Observable<number>;

  private readonly MOBILE_BREAKPOINT = 768;
  private readonly COMPACT_WIDTH_BREAKPOINT = 1024;
  private readonly COMPACT_HEIGHT_BREAKPOINT = 700;

  constructor(
    private readonly router: Router,
    private readonly authHelper: AuthHelper,
    private readonly toastService: ToastService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.unreadCount$ = this.notificationsService.unreadCount$;
  }

  private updateCompactState(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.isCompact =
      window.innerWidth <= this.COMPACT_WIDTH_BREAKPOINT ||
      window.innerHeight <= this.COMPACT_HEIGHT_BREAKPOINT;
  }

  toggleSidebar(): void {
    this.isExpanded = !this.isExpanded;
    if (isPlatformBrowser(this.platformId)) {
      if (window.innerWidth <= this.MOBILE_BREAKPOINT) {
        document.body.style.overflow = this.isExpanded ? 'hidden' : 'auto';
      }
      if (window.innerWidth > this.MOBILE_BREAKPOINT) {
        localStorage.setItem('sidebarExpanded', this.isExpanded.toString());
      }
    }
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const isSidebarClick = target.closest('.sidebar');
    const isMenuButtonClick = target.closest('.mobile-menu-button');

    if (this.isExpanded && !isSidebarClick && !isMenuButtonClick) {
      this.toggleSidebar();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    if (isPlatformBrowser(this.platformId)) {
      this.updateCompactState();
      if (window.innerWidth <= this.MOBILE_BREAKPOINT && this.isExpanded) {
        this.isExpanded = false;
        document.body.style.overflow = 'auto';
      }
    }
  }

  @HostListener('document:keydown.escape')
  onEscapePress() {
    if (this.isExpanded) {
      this.toggleSidebar();
    }
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.updateCompactState();
      if (window.innerWidth > this.MOBILE_BREAKPOINT) {
        const savedState = localStorage.getItem('sidebarExpanded');
        if (savedState) {
          this.isExpanded = savedState === 'true';
        }
      }
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = 'auto';
    }
  }

  public onLogout(): void {
    this.authHelper.logoutSession();
    this.router.navigateByUrl('home');
    this.toastService.showInfo(
      this.translate.instant('sidebar.logoutTitle'),
      this.translate.instant('sidebar.logoutMessage')
    );
  }

  public onFeedback(): void {
    this.router.navigate(['comments']);
  }

  public get isAdmin(): boolean {
    const userToken = this.authHelper.getUserToken();
    return userToken?.user?.role === UserRole.admin;
  }

  public get isStudentOrAdmin(): boolean {
    const userToken = this.authHelper.getUserToken();
    return userToken?.user?.role === UserRole.student || userToken?.user?.role === UserRole.admin;
  }

  public get isTeacherOrAdmin(): boolean {
    const userToken = this.authHelper.getUserToken();
    return userToken?.user?.role === UserRole.teacher || userToken?.user?.role === UserRole.admin || userToken?.user?.role === UserRole.collaborator;
  }

  public openVitalSignsMonitor(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.open(this.vitalSignsUrl, '_blank');
    }
  }

  public openEegMonitor(): void {
    if (isPlatformBrowser(this.platformId)) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/monitor/eeg'])
      );
      window.open(url, '_blank');
    }
  }

  public get showElearning(): boolean {
    const isTeacherOrAdmin = this.isTeacherOrAdmin;
    return isTeacherOrAdmin;
  }

  public get showPremiumElearning(): boolean {
    const userToken = this.authHelper.getUserToken();
    const role = userToken?.user?.role;
    return role === UserRole.viewer || role === UserRole.admin;
  }

}
